import { LayerDto, TextureDto } from "@/types/data-types";
import { CameraControls } from "@react-three/drei";
import gsap from "gsap";
import * as THREE from "three";

export interface HengeCameraControls extends CameraControls {
  autoRotate: boolean;
  distanceToHenge: number;
}

export interface HngVector2 {
  x: number;
  y: number;
}

export interface HngVector3 {
  x: number;
  y: number;
  z: number;
}

export interface HngMatrix2 {
  length: number;
  matrix: number[][];
}

export interface RowColumnCoverageMatrix {
  row: [number, number];
  column: [number, number];
}

export function changeMaterialOpacity(
  object: THREE.Object3D | undefined | null,
  opacity: number,
): void {
  if (!object) return;

  const _opacity = Math.min(1, Math.max(0, opacity));

  // object.visible = _opacity !== 0;

  object.traverse((mesh) => {
    if (!(mesh as THREE.Mesh).isMesh) return;

    (mesh as THREE.Mesh).castShadow = _opacity !== 0;
    (mesh as THREE.Mesh).receiveShadow = _opacity !== 0;

    if (!(mesh as THREE.Mesh).material) return;

    if (Array.isArray((mesh as THREE.Mesh).material)) {
      ((mesh as THREE.Mesh).material as THREE.Material[]).forEach(
        (material) => {
          if (material.isMaterial) {
            if (material.userData.gsap?.kill) {
              material.userData.gsap.kill();
            }

            material.transparent = true;
            material.opacity = _opacity;
          }
        },
      );
    } else if (((mesh as THREE.Mesh).material as THREE.Material).isMaterial) {
      if (
        ((mesh as THREE.Mesh).material as THREE.Material).userData.gsap?.kill
      ) {
        ((mesh as THREE.Mesh).material as THREE.Material).userData.gsap.kill();
      }

      ((mesh as THREE.Mesh).material as THREE.Material).transparent = true;
      ((mesh as THREE.Mesh).material as THREE.Material).opacity = _opacity;
    }
  });
}

export function makeMaterialOpaque(
  object: THREE.Object3D | undefined | null,
): void {
  return changeMaterialOpacity(object, 1);
}

export function makeMaterialTransparent(
  object: THREE.Object3D | undefined | null,
): void {
  return changeMaterialOpacity(object, 0);
}

export function changeMaterialOpacityByGsap(
  object: THREE.Object3D | undefined | null,
  opacity: number,
  delay: number,
  duration: number,
): void {
  if (!object) return;

  const _opacity = Math.min(1, Math.max(0, opacity));

  // object.visible = true;

  object.traverse((mesh) => {
    if (!(mesh as THREE.Mesh).isMesh) return;

    (mesh as THREE.Mesh).castShadow = _opacity !== 0;
    (mesh as THREE.Mesh).receiveShadow = _opacity !== 0;

    if (!(mesh as THREE.Mesh).material) return;

    if (Array.isArray((mesh as THREE.Mesh).material)) {
      ((mesh as THREE.Mesh).material as THREE.Material[]).forEach(
        (material) => {
          if (material.isMaterial) {
            if (material.userData.gsap?.kill) {
              material.userData.gsap.kill();
            }

            material.transparent = true;
            material.userData.gsap = gsap.to(material, {
              delay: delay,
              duration: duration,
              opacity: _opacity,
            });
            // .then((result) => {
            //   if (_opacity === 0) {
            //     object.visible = false;
            //   }
            // });
          }
        },
      );
    } else {
      if (((mesh as THREE.Mesh).material as THREE.Material).isMaterial) {
        if (
          ((mesh as THREE.Mesh).material as THREE.Material).userData.gsap?.kill
        ) {
          (
            (mesh as THREE.Mesh).material as THREE.Material
          ).userData.gsap.kill();
        }

        ((mesh as THREE.Mesh).material as THREE.Material).transparent = true;
        ((mesh as THREE.Mesh).material as THREE.Material).userData.gsap =
          gsap.to((mesh as THREE.Mesh).material as THREE.Material, {
            delay: delay,
            duration: duration,
            opacity: _opacity,
          });
        // .then((result) => {
        //   if (_opacity === 0) {
        //     object.visible = false;
        //   }
        // });
      }
    }
  });
}

export function makeMaterialOpaqueByGsap(
  object: THREE.Object3D | undefined | null,
  delay: number,
  duration: number,
): void {
  return changeMaterialOpacityByGsap(object, 1, delay, duration);
}

export function makeMaterialTransparentByGsap(
  object: THREE.Object3D | undefined | null,
  delay: number,
  duration: number,
): void {
  return changeMaterialOpacityByGsap(object, 0, delay, duration);
}

export function getVertexCount(object: THREE.Object3D) {
  let count = 0;

  object.traverse((o) => {
    if ("geometry" in o) {
      const geometry: THREE.BufferGeometry = o.geometry as THREE.BufferGeometry;

      // BufferGeometry 확인
      const positionAttribute = geometry.attributes.position;
      if (positionAttribute) {
        count += positionAttribute.count; // 정점의 개수
      }
    }
  });

  return count;
}

export function getPointerFromCoordinate(
  x: number,
  width: number,
  y: number,
  height: number,
): THREE.Vector2 {
  return new THREE.Vector2((x / width) * 2 - 1, (y / height) * -2 + 1);
}

export function getCoordinateFromPointer(
  position: THREE.Vector3,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
) {
  // position은 THREE.Vector3
  const vector = position.clone();

  // 3D 좌표를 표준화된 장치 좌표(NDC)로 변환
  vector.project(camera);

  // NDC 좌표를 화면 좌표로 변환
  const x = (vector.x * 0.5 + 0.5) * canvas.clientWidth;
  const y = (-vector.y * 0.5 + 0.5) * canvas.clientHeight;

  return new THREE.Vector2(x, y);
}

export function injectLayersData(root: THREE.Object3D) {
  const depthIndexMap = new Map(); // 각 depth별 현재 index를 추적

  function traverse(node: THREE.Object3D, depth = 0, path = "") {
    // 현재 depth의 index 계산
    if (!depthIndexMap.has(depth)) {
      depthIndexMap.set(depth, 0);
    }
    const currentIndex = depthIndexMap.get(depth);
    depthIndexMap.set(depth, currentIndex + 1);

    // 현재 노드에 depth와 index 정보 추가
    const nodeWithDepth: LayerDto = {
      type: node.type,
      depth,
      idx: currentIndex,
      path: !path ? "0" : `${path}_${currentIndex}`,
      name: node.name,
      originalName: node.userData.name ?? node.name,
    };
    node.userData.layer = nodeWithDepth;

    // 자식 노드들을 재귀적으로 순회
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        traverse(child, depth + 1, nodeWithDepth.path);
      });
    }
  }

  traverse(root);
}

export function traverseLayers(root: THREE.Object3D) {
  const result: LayerDto[] = [];
  const depthIndexMap = new Map(); // 각 depth별 현재 index를 추적

  function traverse(node: THREE.Object3D, depth = 0, path = "") {
    // 현재 depth의 index 계산
    if (!depthIndexMap.has(depth)) {
      depthIndexMap.set(depth, 0);
    }
    const currentIndex = depthIndexMap.get(depth);
    depthIndexMap.set(depth, currentIndex + 1);

    // 현재 노드에 depth와 index 정보 추가
    const nodeWithDepth: LayerDto = {
      type: node.type,
      depth,
      idx: currentIndex,
      path: !path ? "0" : `${path}_${currentIndex}`,
      name: node.name,
      originalName: node.userData.name ?? node.name,
    };
    node.userData.layer = nodeWithDepth;

    if ((node as THREE.Mesh).isMesh) {
      const material = (node as THREE.Mesh).material;
      if (
        material &&
        (material as THREE.MeshPhysicalMaterial).isMeshPhysicalMaterial
      ) {
        nodeWithDepth.material = {
          side: (material as THREE.MeshPhysicalMaterial).side,
          color: `#${(material as THREE.MeshPhysicalMaterial).color.getHexString()}`,
          opacity: (material as THREE.MeshPhysicalMaterial).opacity,
          roughness: (material as THREE.MeshPhysicalMaterial).roughness,
          metalness: (material as THREE.MeshPhysicalMaterial).metalness,
          emissive: `#${(
            material as THREE.MeshPhysicalMaterial
          ).emissive.getHexString()}`,
        };

        const baseColorMap = (material as THREE.MeshPhysicalMaterial).map;
        if (baseColorMap && baseColorMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            baseColorMap: {
              name: `${nodeWithDepth.path}-baseColorMap`,
              url: baseColorMap.image.src,
              thumbnailUrl: baseColorMap.image.src,
            },
          };
        }
        const metalnessMap = (material as THREE.MeshPhysicalMaterial)
          .metalnessMap;
        if (metalnessMap && metalnessMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            metalnessMap: {
              name: `${nodeWithDepth.path}-metalnessMap`,
              url: metalnessMap.image.src,
              thumbnailUrl: metalnessMap.image.src,
            },
          };
        }
        const roughnessMap = (material as THREE.MeshPhysicalMaterial)
          .roughnessMap;
        if (roughnessMap && roughnessMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            roughnessMap: {
              name: `${nodeWithDepth.path}-roughnessMap`,
              url: roughnessMap.image.src,
              thumbnailUrl: roughnessMap.image.src,
            },
          };
        }
        const normalMap = (material as THREE.MeshPhysicalMaterial).normalMap;
        if (normalMap && normalMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            normalMap: {
              name: `${nodeWithDepth.path}-normalMap`,
              url: normalMap.image.src,
              thumbnailUrl: normalMap.image.src,
            },
          };
        }
        const bumpMap = (material as THREE.MeshPhysicalMaterial).bumpMap;
        if (bumpMap && bumpMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            bumpMap: {
              name: `${nodeWithDepth.path}-bumpMap`,
              url: bumpMap.image.src,
              thumbnailUrl: bumpMap.image.src,
            },
          };
        }
        const displacementMap = (material as THREE.MeshPhysicalMaterial)
          .displacementMap;
        if (displacementMap && displacementMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            displacementMap: {
              name: `${nodeWithDepth.path}-displacementMap`,
              url: displacementMap.image.src,
              thumbnailUrl: displacementMap.image.src,
            },
          };
        }
        const aoMap = (material as THREE.MeshPhysicalMaterial).aoMap;
        if (aoMap && aoMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            aoMap: {
              name: `${nodeWithDepth.path}-aoMap`,
              url: aoMap.image.src,
              thumbnailUrl: aoMap.image.src,
            },
          };
        }
        const emissiveMap = (material as THREE.MeshPhysicalMaterial)
          .emissiveMap;
        if (emissiveMap && emissiveMap.image.src) {
          nodeWithDepth.texture = {
            ...nodeWithDepth.texture,
            emissiveMap: {
              name: `${nodeWithDepth.path}-emissiveMap`,
              url: emissiveMap.image.src,
              thumbnailUrl: emissiveMap.image.src,
            },
          };
        }
      }
    }
    result.push(nodeWithDepth);

    // 자식 노드들을 재귀적으로 순회
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        traverse(child, depth + 1, nodeWithDepth.path);
      });
    }
  }

  traverse(root);
  return result;
}

export async function loadTexture(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(
      url,
      (texture) => {
        texture.needsUpdate = true;
        resolve(texture);
      },
      undefined,
      (error) => reject(error),
    );
  });
}

type TextureResult = {
  [K in keyof TextureDto as `${K}Texture`]: THREE.Texture | undefined;
};

export async function loadPBRTextureMaps(
  maps: Partial<TextureDto>,
): Promise<TextureResult> {
  const texturePromises = Object.entries(maps)
    .filter(([key]) => key.endsWith("Map"))
    .map(async ([key, textureMap]) => {
      if (textureMap) {
        try {
          const texture = await loadTexture(textureMap.url);
          texture.flipY = false;
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.generateMipmaps = true;
          texture.minFilter = THREE.LinearMipMapLinearFilter;
          return [key + "Texture", texture];
        } catch (error) {
          console.error(`Failed to load texture for ${key}:`, error);
          return [key + "Texture", undefined];
        }
      }
      return [key + "Texture", undefined];
    });

  const results = await Promise.all(texturePromises);
  return Object.fromEntries(results) as TextureResult;
}

export function dataURLtoFile(dataUrl: string, filename: string) {
  // dataURL 부분에서 실제 base64 문자열 추출
  const arr = dataUrl.split(",");
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  // Blob 생성
  const blob = new Blob([u8arr], { type: "image/png" });

  // File 객체 생성 및 반환
  return new File([blob], filename, { type: "image/png" });
}
